/**
 * Product Type Form Component
 * Form for creating and editing product types with variants
 */

import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  TagIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import VariantForm from './VariantForm';

const ProductTypeForm = ({ 
  productType = null, 
  onSubmit, 
  onClose 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    minimumRentalDuration: 1,
    rates: [{ rate: '', rateType: 'HOURLY' }],
    variants: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVariantForm, setShowVariantForm] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [editingVariantIndex, setEditingVariantIndex] = useState(-1);

  // Initialize form with product type data if editing
  useEffect(() => {
    if (productType) {
      setFormData({
        name: productType.name || '',
        description: productType.description || '',
        category: productType.category || '',
        minimumRentalDuration: productType.minimumRentalDuration || 1,
        rates: productType.rates && productType.rates.length > 0
          ? productType.rates.map(rate => ({
              rate: rate.rate ? rate.rate.toString() : '',
              rateType: rate.rateType || 'HOURLY'
            }))
          : [{ rate: '', rateType: 'HOURLY' }],
        variants: productType.variants || []
      });
    }
  }, [productType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRateChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      rates: prev.rates.map((rate, i) =>
        i === index ? { ...rate, [field]: value } : rate
      )
    }));
  };

  const addRate = () => {
    setFormData(prev => ({
      ...prev,
      rates: [...prev.rates, { rate: '', rateType: 'HOURLY' }]
    }));
  };

  const removeRate = (index) => {
    if (formData.rates.length > 1) {
      setFormData(prev => ({
        ...prev,
        rates: prev.rates.filter((_, i) => i !== index)
      }));
    }
  };

  const handleAddVariant = () => {
    setEditingVariant(null);
    setEditingVariantIndex(-1);
    setShowVariantForm(true);
  };

  const handleEditVariant = (variant, index) => {
    setEditingVariant(variant);
    setEditingVariantIndex(index);
    setShowVariantForm(true);
  };

  const handleVariantSubmit = (variantData) => {
    setFormData(prev => {
      const newVariants = [...prev.variants];
      
      if (editingVariantIndex >= 0) {
        // Edit existing variant
        newVariants[editingVariantIndex] = variantData;
      } else {
        // Add new variant
        newVariants.push(variantData);
      }
      
      return {
        ...prev,
        variants: newVariants
      };
    });
    
    setShowVariantForm(false);
    setEditingVariant(null);
    setEditingVariantIndex(-1);
  };

  const handleRemoveVariant = (index) => {
    if (window.confirm('Are you sure you want to remove this variant?')) {
      setFormData(prev => ({
        ...prev,
        variants: prev.variants.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate required fields
    if (!formData.name.trim()) {
      alert('Please enter a product type name');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🔍 ProductTypeForm - Submitting formData:', formData);
      console.log('🔍 ProductTypeForm - Variants being submitted:', formData.variants);
      console.log('🔍 ProductTypeForm - Number of variants:', formData.variants?.length || 0);
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatVariantRates = (variant) => {
    if (!variant.rates || variant.rates.length === 0) {
      return 'No rates';
    }
    
    const rateLabels = {
      'HOURLY': '/hr',
      'DAILY': '/day',
      'WEEKLY': '/week',
      'MONTHLY': '/month',
      'FLAT_RATE': ' flat',
      'PER_PERSON': '/person',
      'FIXED': ' fixed'
    };
    
    return variant.rates.map(rate => 
      `$${parseFloat(rate.rate).toFixed(2)}${rateLabels[rate.rateType] || ''}`
    ).join(', ');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end sm:items-center justify-center min-h-screen pt-4 px-2 sm:px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Modal panel - Mobile Optimized */}
        <div className="inline-block align-bottom bg-white rounded-t-lg sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full w-full max-h-[90vh] sm:max-h-none">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 flex-1 overflow-y-auto">
              {/* Header - Mobile Optimized */}
              <div className="flex items-center justify-between mb-4 sm:mb-6 sticky top-0 bg-white pb-2 border-b sm:border-b-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                  <CubeIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                  <span className="truncate">
                    {productType ? 'Edit Product Type' : 'Create New Product Type'}
                  </span>
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 p-2 sm:p-1 -m-2 sm:m-0 touch-manipulation"
                  aria-label="Close form"
                >
                  <XMarkIcon className="h-6 w-6 sm:h-5 sm:w-5" />
                </button>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {/* Basic Information - Mobile Optimized */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Type Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                      placeholder="Enter product type name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <TagIcon className="h-4 w-4 inline mr-1" />
                      Category
                    </label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                      placeholder="e.g., Vehicles, Equipment, etc."
                    />
                  </div>
                </div>

                {/* Description - Mobile Optimized */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows="4"
                    className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation resize-none sm:resize-y"
                    placeholder="Describe this product type..."
                  />
                </div>

                {/* Pricing Options - Mobile Optimized */}
                <div className="border-t border-gray-200 pt-4 sm:pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 space-y-3 sm:space-y-0">
                    <h3 className="text-lg font-medium text-gray-900">Pricing Options</h3>
                    <button
                      type="button"
                      onClick={addRate}
                      className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 sm:px-3 sm:py-2 border border-transparent text-base sm:text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 touch-manipulation"
                    >
                      <PlusIcon className="h-5 w-5 sm:h-4 sm:w-4 mr-2 sm:mr-1" />
                      Add Rate
                    </button>
                  </div>

                  {/* Minimum Rental Duration */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Rental Duration (hours)
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={formData.minimumRentalDuration}
                      onChange={(e) => handleInputChange('minimumRentalDuration', parseFloat(e.target.value) || 1)}
                      className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                      placeholder="1"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Applies only to hourly rate type. Default is 1 hour.
                    </p>
                  </div>

                  {/* Base Rates List */}
                  {formData.rates && formData.rates.length > 0 && (
                    <div className="space-y-3">
                      {formData.rates.map((rate, index) => (
                        <div key={index} className="flex flex-col sm:flex-row gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Rate Amount ($)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={rate.rate}
                              onChange={(e) => handleRateChange(index, 'rate', e.target.value)}
                              className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                              placeholder="25.00"
                            />
                          </div>

                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Rate Type
                            </label>
                            <select
                              value={rate.rateType}
                              onChange={(e) => handleRateChange(index, 'rateType', e.target.value)}
                              className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                            >
                              <option value="HOURLY">Hourly</option>
                              <option value="DAILY">Daily</option>
                              <option value="WEEKLY">Weekly</option>
                            </select>
                          </div>

                          {formData.rates.length > 1 && (
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={() => removeRate(index)}
                                className="p-2 sm:p-2.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors touch-manipulation"
                                title="Remove rate"
                              >
                                <TrashIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Variants Section - Mobile Optimized */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 space-y-3 sm:space-y-0">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Variants</h3>
                      <p className="text-sm text-gray-500">
                        Create different pricing options for this product type
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddVariant}
                      className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 sm:px-3 sm:py-2 border border-transparent text-base sm:text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 touch-manipulation"
                    >
                      <PlusIcon className="h-5 w-5 sm:h-4 sm:w-4 mr-2 sm:mr-1" />
                      Add Variant
                    </button>
                  </div>

                  {/* Variants List - Mobile Optimized */}
                  {formData.variants.length > 0 ? (
                    <div className="space-y-3">
                      {formData.variants.map((variant, index) => (
                        <div
                          key={index}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3 sm:space-y-0"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{variant.name}</div>
                            <div className="text-sm text-gray-600 mt-1">
                              {formatVariantRates(variant)}
                            </div>
                          </div>

                          <div className="flex items-center justify-end space-x-3 sm:space-x-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-200">
                            <button
                              type="button"
                              onClick={() => handleEditVariant(variant, index)}
                              className="flex-1 sm:flex-none text-center sm:text-left text-blue-600 hover:text-blue-700 text-sm font-medium py-2 sm:py-0 px-4 sm:px-0 touch-manipulation"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveVariant(index)}
                              className="p-2 text-red-400 hover:text-red-600 touch-manipulation"
                              title="Remove variant"
                              aria-label="Remove variant"
                            >
                              <TrashIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h4 className="mt-2 text-sm font-medium text-gray-900">No variants yet</h4>
                      <p className="mt-1 text-sm text-gray-500 px-4">
                        Add your first variant to start configuring pricing options.
                      </p>
                      <button
                        type="button"
                        onClick={handleAddVariant}
                        className="mt-4 w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 sm:px-3 sm:py-2 border border-transparent text-base sm:text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 touch-manipulation"
                      >
                        <PlusIcon className="h-5 w-5 sm:h-4 sm:w-4 mr-2 sm:mr-1" />
                        Add First Variant
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions - Mobile Optimized */}
            <div className="bg-gray-50 px-4 py-4 sm:px-6 sm:py-3 sm:flex sm:flex-row-reverse border-t">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-3 sm:py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                {isSubmitting ? 'Saving...' : (productType ? 'Update Product Type' : 'Create Product Type')}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-3 sm:py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Variant Form Modal */}
      {showVariantForm && (
        <VariantForm
          variant={editingVariant}
          onSubmit={handleVariantSubmit}
          onClose={() => {
            setShowVariantForm(false);
            setEditingVariant(null);
            setEditingVariantIndex(-1);
          }}
        />
      )}
    </div>
  );
};

export default ProductTypeForm;